import {
	type ReviewableRealtorProfile,
	USER_TYPES,
	type UserType,
} from "@shared/types";
import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@shared-ui/sheet";
import { Skeleton } from "@shared-ui/skeleton";
import { Textarea } from "@shared-ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
	ContractorReviewDetails,
	type ReviewableContractorProfile,
} from "@/features/approvals/components/contractor-review-details";
import {
	DesignerReviewDetails,
	type ReviewableDesignerProfile,
} from "@/features/approvals/components/designer-review-details";
import { RealtorReviewDetails } from "@/features/approvals/components/realtor-review-details";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { hasPermission } from "@/lib/permissions";
import { useDebounce } from "@/lib/use-debounce";
import {
	type AdminUserRecord,
	changeUserType,
	getAdminUser,
	listAdminUsers,
	reactivateUser,
	resendOtp,
	suspendUser,
} from "../services/users-api.service";

/*
 * Rendered from USER_TYPES, not written out.
 *
 * Both dropdowns listed homeowner/designer/contractor by hand, so `realtor`
 * was unreachable from the admin app -- an operator could not move anyone to
 * it, and could not filter for one. The same drift the backend carried; the
 * shared const tuple is the fix in both places.
 */
const USER_TYPE_LABELS: Record<UserType, string> = {
	homeowner: "Homeowner",
	designer: "Designer",
	contractor: "Contractor",
	realtor: "Realtor",
};

/**
 * A realtor's profile as the user detail returns it: the full realtor profile
 * row, the same shape the approvals queue reads, so the Users page shows the
 * same RERA, practice and ID proof details the reviewer sees.
 */
function realtorProfileOf(
	record: AdminUserRecord,
): ReviewableRealtorProfile | null {
	if (record.user.userType !== "realtor" || !record.profile) return null;
	return record.profile as ReviewableRealtorProfile;
}

/**
 * A designer's profile as the user detail returns it: the full
 * `designerProfiles` row, the same shape the approvals queue reads. Cast for
 * the same reason as `realtorProfileOf` above -- `AdminUserRecord.profile`
 * stays narrow on purpose, and the endpoint is known to send the full row.
 */
function designerProfileOf(
	record: AdminUserRecord,
): ReviewableDesignerProfile | null {
	if (record.user.userType !== "designer" || !record.profile) return null;
	return record.profile as ReviewableDesignerProfile;
}

/**
 * A contractor's profile as the user detail returns it: the full
 * `contractorProfiles` row, the same shape the approvals queue reads. Cast
 * for the same reason as `realtorProfileOf` above -- `AdminUserRecord.profile`
 * stays narrow on purpose, and the endpoint is known to send the full row.
 */
function contractorProfileOf(
	record: AdminUserRecord,
): ReviewableContractorProfile | null {
	if (record.user.userType !== "contractor" || !record.profile) return null;
	return record.profile as ReviewableContractorProfile;
}

export function UsersDashboardPage() {
	const queryClient = useQueryClient();
	const operator = useAdminAuthStore((state) => state.operator);
	const [query, setQuery] = useState("");
	const debouncedQuery = useDebounce(query, 250);
	const [userType, setUserType] = useState("all");
	const [isActive, setIsActive] = useState("all");
	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
	const [reason, setReason] = useState("");
	const [nextUserType, setNextUserType] = useState<UserType>("homeowner");

	const usersQuery = useQuery({
		queryKey: ["admin-users", debouncedQuery, userType, isActive],
		queryFn: () =>
			listAdminUsers({
				query: debouncedQuery || undefined,
				userType: userType === "all" ? undefined : userType,
				isActive: isActive === "all" ? undefined : isActive === "active",
			}),
		placeholderData: (previous) => previous,
	});

	const detailQuery = useQuery({
		queryKey: ["admin-user-detail", selectedUserId],
		queryFn: () => {
			if (!selectedUserId) throw new Error("No user ID selected");
			return getAdminUser(selectedUserId);
		},
		enabled: !!selectedUserId,
	});
	const realtorProfile = detailQuery.data
		? realtorProfileOf(detailQuery.data)
		: null;
	const designerProfile = detailQuery.data
		? designerProfileOf(detailQuery.data)
		: null;
	const contractorProfile = detailQuery.data
		? contractorProfileOf(detailQuery.data)
		: null;

	const invalidateUsers = async () => {
		await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
		await queryClient.invalidateQueries({
			queryKey: ["admin-user-detail", selectedUserId],
		});
	};

	const suspendMutation = useMutation({
		mutationFn: (input: { userId: string; reason: string }) =>
			suspendUser(input.userId, input.reason),
		onSuccess: async () => {
			await invalidateUsers();
			toast.success("User suspended");
			setReason("");
		},
	});

	const reactivateMutation = useMutation({
		mutationFn: (input: { userId: string; reason: string }) =>
			reactivateUser(input.userId, input.reason),
		onSuccess: async () => {
			await invalidateUsers();
			toast.success("User reactivated");
			setReason("");
		},
	});

	const typeMutation = useMutation({
		mutationFn: (input: {
			userId: string;
			userType: UserType;
			reason: string;
		}) => changeUserType(input.userId, input.userType, input.reason),
		onSuccess: async () => {
			await invalidateUsers();
			toast.success("User type updated");
			setReason("");
		},
	});

	const resendMutation = useMutation({
		mutationFn: (input: { userId: string; reason?: string }) =>
			resendOtp(input.userId, input.reason),
		onSuccess: () => {
			toast.success("OTP resent");
		},
	});

	return (
		<div className="grid gap-5">
			<div className="grid gap-2">
				<p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
					User operations
				</p>
				<h2 className="text-2xl font-semibold tracking-tight">Accounts desk</h2>
				<p className="text-sm text-muted-foreground">
					Search any user, inspect payment and project context, and perform
					safety-checked account actions.
				</p>
			</div>

			<Card className="border-slate-200 shadow-none">
				<CardHeader>
					<CardTitle>Users</CardTitle>
					<CardDescription>
						Hard suspend uses the existing `isActive` auth gate, while user-type
						fixes stay blocked for high-risk accounts.
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4">
					<div className="grid gap-3 rounded-2xl border bg-slate-50/80 p-3 md:grid-cols-[minmax(0,1fr)_220px_220px]">
						<Input
							placeholder="Search by phone, id, or profile name"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
						/>
						<Select value={userType} onValueChange={setUserType}>
							<SelectTrigger>
								<SelectValue placeholder="User type" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectItem value="all">All user types</SelectItem>
									{USER_TYPES.map((type) => (
										<SelectItem key={type} value={type}>
											{USER_TYPE_LABELS[type]}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
						<Select value={isActive} onValueChange={setIsActive}>
							<SelectTrigger>
								<SelectValue placeholder="Status" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectItem value="all">All states</SelectItem>
									<SelectItem value="active">Active</SelectItem>
									<SelectItem value="inactive">Suspended</SelectItem>
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>

					{usersQuery.isLoading ? (
						<Skeleton className="h-72 w-full rounded-2xl" />
					) : usersQuery.isError ? (
						<div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-10 text-sm text-rose-700">
							Unable to load users. Your admin session may have expired. Sign in
							again and retry.
						</div>
					) : (
						<div className="overflow-hidden rounded-2xl border">
							<table className="w-full text-left text-sm">
								<thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-muted-foreground">
									<tr>
										<th className="px-4 py-3">Phone</th>
										<th className="px-4 py-3">Type</th>
										<th className="px-4 py-3">State</th>
										<th className="px-4 py-3">Projects</th>
										<th className="px-4 py-3">Payments</th>
									</tr>
								</thead>
								<tbody>
									{usersQuery.data?.map((entry) => (
										<tr
											key={entry.user.id}
											className="cursor-pointer border-t hover:bg-slate-50"
											onClick={() => {
												setSelectedUserId(entry.user.id);
												setNextUserType(
													(entry.user.userType as UserType) || "homeowner",
												);
											}}
										>
											<td className="px-4 py-3">
												<div className="grid gap-1">
													<span className="font-medium">
														{entry.user.phone}
													</span>
													<span className="text-xs text-muted-foreground">
														{entry.profile?.name || "No profile yet"}
													</span>
												</div>
											</td>
											<td className="px-4 py-3">
												<Badge>{entry.user.userType || "unset"}</Badge>
											</td>
											<td className="px-4 py-3">
												<Badge
													variant={entry.user.isActive ? "success" : "danger"}
												>
													{entry.user.isActive ? "active" : "suspended"}
												</Badge>
											</td>
											<td className="px-4 py-3">{entry.projectCount}</td>
											<td className="px-4 py-3">{entry.paymentCount}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			<Sheet
				open={!!selectedUserId}
				onOpenChange={(open) => !open && setSelectedUserId(null)}
			>
				<SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
					{detailQuery.isLoading ? (
						<Skeleton className="h-full w-full rounded-2xl" />
					) : detailQuery.data ? (
						<div className="grid gap-5">
							<SheetHeader>
								<SheetTitle>{detailQuery.data.user.phone}</SheetTitle>
							</SheetHeader>
							<Card className="border-slate-200 shadow-none">
								<CardHeader>
									<CardTitle>User context</CardTitle>
								</CardHeader>
								<CardContent className="grid gap-4 text-sm">
									<div>
										<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
											User ID
										</p>
										<div className="mt-1 flex items-center gap-2">
											<code className="font-mono text-xs">
												{detailQuery.data.user.id}
											</code>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => {
													navigator.clipboard.writeText(
														detailQuery.data.user.id,
													);
													toast.success("User ID copied");
												}}
											>
												<Copy className="h-3.5 w-3.5" />
											</Button>
										</div>
										<p className="mt-1 text-xs text-muted-foreground">
											Needed for the Roles &amp; Permissions page.
										</p>
									</div>
									<div className="grid gap-2 md:grid-cols-2">
										<div>
											<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
												User type
											</p>
											<p className="mt-1 font-medium">
												{detailQuery.data.user.userType || "unset"}
											</p>
										</div>
										<div>
											<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
												Status
											</p>
											<div className="mt-1">
												<Badge
													variant={
														detailQuery.data.user.isActive
															? "success"
															: "danger"
													}
												>
													{detailQuery.data.user.isActive
														? "active"
														: "suspended"}
												</Badge>
											</div>
										</div>
										<div>
											<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
												Projects
											</p>
											<p className="mt-1 font-medium">
												{detailQuery.data.projectCount}
											</p>
										</div>
										<div>
											<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
												Payments
											</p>
											<p className="mt-1 font-medium">
												{detailQuery.data.paymentCount}
											</p>
										</div>
									</div>
									{detailQuery.data.profile ? (
										<div className="rounded-2xl bg-slate-50 p-4">
											<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
												Profile
											</p>
											<p className="mt-2 font-medium">
												{detailQuery.data.profile.name || "Unnamed profile"}
											</p>
											{detailQuery.data.profile.reviewStatus ? (
												<div className="mt-2">
													<Badge>{detailQuery.data.profile.reviewStatus}</Badge>
												</div>
											) : null}
										</div>
									) : null}
								</CardContent>
							</Card>

							{realtorProfile ? (
								<RealtorReviewDetails
									profile={realtorProfile}
									canViewIdProof={hasPermission(
										operator,
										"admin:approvals:read",
									)}
								/>
							) : null}

							{designerProfile ? (
								<DesignerReviewDetails profile={designerProfile} />
							) : null}

							{contractorProfile ? (
								<ContractorReviewDetails profile={contractorProfile} />
							) : null}

							<Card className="border-slate-200 shadow-none">
								<CardHeader>
									<CardTitle>Operator action</CardTitle>
									<CardDescription>
										All writes require a reason and are recorded in admin action
										logs.
									</CardDescription>
								</CardHeader>
								<CardContent className="grid gap-4">
									<Textarea
										rows={4}
										value={reason}
										onChange={(event) => setReason(event.target.value)}
										placeholder="Record why this action is necessary."
									/>
									<div className="grid gap-3 md:grid-cols-2">
										<Button
											variant={
												detailQuery.data.user.isActive ? "outline" : "default"
											}
											onClick={() =>
												(detailQuery.data?.user.isActive
													? suspendMutation
													: reactivateMutation
												).mutate({
													userId: detailQuery.data?.user.id,
													reason,
												})
											}
											isLoading={
												suspendMutation.isPending ||
												reactivateMutation.isPending
											}
											disabled={reason.trim().length < 5}
										>
											{detailQuery.data.user.isActive
												? "Suspend user"
												: "Reactivate user"}
										</Button>
										<Button
											variant="outline"
											onClick={() =>
												resendMutation.mutate({
													userId: detailQuery.data?.user.id,
													reason,
												})
											}
											isLoading={resendMutation.isPending}
										>
											Resend OTP
										</Button>
									</div>
									<div className="grid gap-3 rounded-2xl bg-slate-50 p-4">
										<p className="text-sm font-medium">
											Manual user-type correction
										</p>
										<Select
											value={nextUserType}
											onValueChange={(value) =>
												setNextUserType(value as UserType)
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectGroup>
													{USER_TYPES.map((type) => (
														<SelectItem key={type} value={type}>
															{USER_TYPE_LABELS[type]}
														</SelectItem>
													))}
												</SelectGroup>
											</SelectContent>
										</Select>
										<Button
											onClick={() =>
												typeMutation.mutate({
													userId: detailQuery.data?.user.id,
													userType: nextUserType,
													reason,
												})
											}
											isLoading={typeMutation.isPending}
											disabled={reason.trim().length < 5}
										>
											Update user type
										</Button>
									</div>
								</CardContent>
							</Card>
						</div>
					) : null}
				</SheetContent>
			</Sheet>
		</div>
	);
}
